// @ts-nocheck

import _ from 'lodash';

import { CommonActionTypes } from '../../../types/actions/common';
import { type BetslipStatus } from '../../../types/betslip';
import { MPStateActionTypes } from '../types/actions/mp';
import type { MPCardStatus } from '../types/cards';
import { type MPFamilyId, type MPFamilyStatus } from '../types/families';
import type { MPGlobalStatus } from '../types/global';
import { type MPUserStatus } from '../types/user';
import { type MPNotificationsType } from '../types/user';

export type MPGlobalState = {
  status?: MPGlobalStatus;
  version?: string | null;
  operationEndDate: Date;
};

export type MPNotification = {
  type: MPNotificationsType;
  uuid: string;
  data: {
    familyId: string;
    cardIndex: number;
  };
};

export type MPMiscState = {
  tmpNotifications?: Array<MPNotification>;
};

export type MPFamilyState = {
  [key in MPFamilyId]: {
    eligibility: {
      hasOutright: boolean;
      hasFreebet: boolean;
      excludedBetTypes: Array<number>;
      minBetOdds: number;
    };
    rules: {
      stakeMultiplicator: number;
    };
  };
};

export type MPUserProgress = {
  [key: MPFamilyId]: {
    status: MPFamilyStatus;
    wonAmount?: number | null;
    cards: {
      [key: string]: {
        status: MPCardStatus;
        bet: string;
      };
    };
  };
};

export type MPUserBetslips = {
  [betslipReference: string]: {
    betslipStatus: BetslipStatus;
    validationDate: number;
    betslipId: number;
    betAmount: number;
    matchIds: Array<number>;
    odds: number;
  };
};

export type MPUserState = {
  status: MPUserStatus;
  progress: MPUserProgress;
  notifications: Array<MPNotification>;
  betslips: MPUserBetslips;
};

export type MPState = {
  global: MPGlobalState;
  familyOrder: Array<MPFamilyId>;
  families: MPFamilyState;
  user: MPUserState;
  misc: MPMiscState;
};

export const mpInitialState: MPState = {
  global: {},
  familyOrder: [],
  families: {},
  user: {},
  misc: {},
};

const actionTypesToReactOn = [
  CommonActionTypes.RECEIVE_ENTITIES,
  MPStateActionTypes.UPDATE_STATE,
  MPStateActionTypes.FILL_TEMPORARY_NOTIFICATIONS,
  MPStateActionTypes.SKIP_ALL_TMP_NOTIFICATIONS,
  MPStateActionTypes.SKIP_CURRENT_TMP_NOTIFICATION,
];

export default (state: MPState = mpInitialState, action: unknown) => {
  const { type, payload } = action;
  const { global, families, familyOrder, user, misc } = state;

  if (actionTypesToReactOn.indexOf(type) !== -1) {
    if (type === MPStateActionTypes.SKIP_ALL_TMP_NOTIFICATIONS) {
      return {
        ...state,
        misc: {
          ...misc,
          tmpNotifications: [],
        },
      };
    }

    if (type === MPStateActionTypes.SKIP_CURRENT_TMP_NOTIFICATION) {
      const { tmpNotifications } = misc;
      if (tmpNotifications) {
        const [, ...next]: Array<unknown> = tmpNotifications;
        const copy = JSON.parse(JSON.stringify(next));

        return {
          ...state,
          misc: {
            ...misc,
            tmpNotifications: copy,
          },
        };
      }
    }

    if (type === MPStateActionTypes.FILL_TEMPORARY_NOTIFICATIONS) {
      const { notifications = [] } = user ?? {};
      const copy = JSON.parse(JSON.stringify(notifications));

      return {
        ...state,
        misc: {
          ...misc,
          tmpNotifications: copy,
        },
      };
    }

    if (type === MPStateActionTypes.UPDATE_STATE) {
      const { userStateMutation } = payload;

      const nextUserState = {};
      _.mergeWith(nextUserState, user, userStateMutation, (objValue, srcValue) => {
        if (_.isArray(srcValue)) {
          return srcValue;
        }
      });

      return {
        ...state,
        user: nextUserState,
      };
    }

    if (type === CommonActionTypes.RECEIVE_ENTITIES) {
      const { settings } = payload;
      const { monopoly } = settings ?? {};
      const { version, status, operationEndDate } = monopoly ?? {};

      return {
        ...state,
        global: {
          ...global,
          status: (status === null || monopoly === null) && global.status ? undefined : status ?? global.status,
          version: (version === null || monopoly === null) && global.version ? undefined : version ?? global.version,
          operationEndDate:
            (operationEndDate === null || monopoly === null) && global.operationEndDate
              ? undefined
              : operationEndDate ?? global.operationEndDate,
        },
        families: (families === null || monopoly === null) && families ? undefined : monopoly?.families ?? families,
        familyOrder:
          (families === null || monopoly === null) && families ? undefined : monopoly?.familyOrder ?? familyOrder,
      };
    }
  }

  return state;
};
